import { c as _c } from "react/compiler-runtime";
import { ErrorBoundary } from "@/components/error-boundary";
import { PromotionalCardRenderer, useRotatingCards } from "@/components/promotional-cards/hooks";
import { PromotionalCardConfig } from "@/components/promotional-cards/types";
import { useMediaQuery } from "@/hooks/use-media-query";
import { useIsMobile } from "@/hooks/use-mobile";
import { useUrlFilters } from "@/hooks/use-url-filters";
import { getCityNameFromCode } from "@/info/cities";
import { useSearchPageMapData } from "@/providers/search";
import { GpsAsLatLng } from "@/types/GpsAsLatLng";
import { MapItemSUP, MapItemWithCoodId } from "@/types/search";
import { getSearchTitle } from "@/utils/search/get-search-title";
import { useVirtualizer } from "@tanstack/react-virtual";
import { LoaderCircleIcon } from "lucide-react";
import { usePathname } from "next/navigation";
import React, { useState } from "react";
import { SearchItem } from "./item";
import { ListSkeleton } from "./skeleton";
interface MobileSearchHeaderProps {
  title: string;
  propertyCount: number;
  totalCount: number | null;
  isLoading?: boolean;
  isAtLimit?: boolean;
  className?: string;
}
function MobileSearchHeader(t0) {
  const $ = _c(16);
  const {
    title,
    propertyCount,
    totalCount,
    isLoading,
    isAtLimit
  } = t0;
  useUrlFilters();
  useState(false);
  let t1;
  if ($[0] !== isLoading || $[1] !== propertyCount || $[2] !== totalCount) {
    t1 = isLoading ? <span className="inline-block h-2.5 bg-gray-300 rounded animate-pulse w-8 align-middle mr-1" /> : <span className="font-semibold">{(totalCount || propertyCount).toLocaleString()}</span>;
    $[0] = isLoading;
    $[1] = propertyCount;
    $[2] = totalCount;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  let t2;
  if ($[4] !== title) {
    t2 = <span className="font-medium">{title}</span>;
    $[4] = title;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  let t3;
  if ($[6] !== propertyCount) {
    t3 = propertyCount.toLocaleString();
    $[6] = propertyCount;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  let t4;
  if ($[8] !== t3) {
    t4 = <span className="font-semibold">{t3}</span>;
    $[8] = t3;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  const t5 = isAtLimit ? "+" : "";
  const t6 = isAtLimit && !isLoading ? ". Zoom in for a better search." : "";
  let t7;
  if ($[10] !== t1 || $[11] !== t2 || $[12] !== t4 || $[13] !== t5 || $[14] !== t6) {
    t7 = <><div className="px-3 py-1.5 bg-transparent"><p className="text-xs text-gray-600">{t1}{" "}{t2}, showing{" "}{t4}{t5} in the visible map area{t6}</p></div></>;
    $[10] = t1;
    $[11] = t2;
    $[12] = t4;
    $[13] = t5;
    $[14] = t6;
    $[15] = t7;
  } else {
    t7 = $[15];
  }
  return t7;
}
interface Params {
  hideType: string;
  milestones: {
    id: string;
    type: string;
    milestone: string;
  }[];
}
export const getMilestones = ({
  milestones,
  hideType
}: Params): {
  id: string;
  type: string;
}[] | undefined => {
  if (hideType === "milestones") return undefined;
  if (hideType === "rejected") milestones = milestones.filter(x => x.milestone !== "rejected");
  return milestones.map(m => ({
    id: m.id,
    type: m.type
  }));
};

// Columns count matches CSS breakpoints exactly
// CSS: grid-cols-1 xl:grid-cols-2 (xl = 1280px)

// Fixed item height for virtualizer
const ITEM_HEIGHT = 400; // Match the h-[370px] + gap + padding
const PROMO_ROW_HEIGHT = 90; // Height for promo card + bottom margin

interface SearchListProps {
  className?: string;
  style?: React.CSSProperties;
}
const ListViewFallback = t0 => {
  const $ = _c(3);
  const {
    children
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <div className="text-center p-6"><div className="text-gray-800 font-semibold mb-2">List View Error</div><p className="text-gray-600 text-sm">Unable to load property list. Try refreshing the page.</p></div>;
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] !== children) {
    t2 = <div className="w-full h-full flex items-center justify-center bg-gray-50 relative">{t1}{children}</div>;
    $[1] = children;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  return t2;
};

// Define the derived item type
type DerivedSupItem = MapItemSUP & {
  mapCoordinates: number[];
  gps?: GpsAsLatLng;
  projectId: string;
  projectName: string;
  localityName: string;
  propertyTypes: string[];
  relatedSups: MapItemSUP[];
  updatedAt?: Date;
};

// Double-layer cache: MapItem -> (Sup -> DerivedItem)
// This ensures that:
// 1. If MapItem reference changes (data update), we get a fresh inner map, so items are recomputed correctly.
// 2. If MapItem is stable, we look up by Sup reference.
// 3. If Sup is stable within stable MapItem, we get cached derived item.
const supItemCache = new WeakMap<MapItemWithCoodId, WeakMap<MapItemSUP, DerivedSupItem>>();
const getSupItem = ({
  mapItem,
  sup
}: {
  mapItem: MapItemWithCoodId;
  sup: MapItemSUP;
}): DerivedSupItem => {
  let innerMap = supItemCache.get(mapItem);
  if (!innerMap) {
    innerMap = new WeakMap();
    supItemCache.set(mapItem, innerMap);
  }
  if (innerMap.has(sup)) {
    return innerMap.get(sup)!;
  }

  // Create new item if not in cache
  const newItem: DerivedSupItem = {
    ...sup,
    mapCoordinates: mapItem._id,
    // Use map item coordinates (number[])
    projectId: mapItem.projectId,
    projectName: mapItem.projectName,
    localityName: mapItem.localityName,
    propertyTypes: mapItem.propertyTypes,
    gps: sup.gps,
    // GPS comes from the sup itself
    updatedAt: sup.updatedAt ? new Date(sup.updatedAt) : undefined,
    relatedSups: mapItem.sups
  };

  // Cache the result
  innerMap.set(sup, newItem);
  return newItem;
};
const getListData = ({
  mapData,
  initialMapData,
  hasFirstClientDataLoaded
}: {
  mapData: MapItemWithCoodId[];
  initialMapData?: MapItemWithCoodId[];
  hasFirstClientDataLoaded?: boolean;
}) => {
  if (!mapData || mapData.length === 0) return [];

  // Track seen SUP ids to prevent duplicates across SSR and client data
  const seenIds = new Set<string>();
  // Only use initialMapData if we haven't loaded client data yet
  const flattenedList = (hasFirstClientDataLoaded ? [] : initialMapData || []).reduce((acc, item) => {
    const sups = item.sups || [];
    for (const sup of sups) {
      if (sup?._id && !seenIds.has(sup._id)) {
        seenIds.add(sup._id);
        acc.push(getSupItem({
          mapItem: item,
          sup
        }));
      }
    }
    return acc;
  }, [] as DerivedSupItem[]);
  mapData.forEach((mapItem: MapItemWithCoodId) => {
    if (mapItem.sups && Array.isArray(mapItem.sups)) {
      mapItem.sups.forEach((sup: MapItemSUP) => {
        if (!sup?._id || seenIds.has(sup._id)) return;
        // Add map item coordinates and project info to each sup
        // Convert updatedAt string to Date object if it exists
        flattenedList.push(getSupItem({
          mapItem,
          sup
        }));
      });
    }
  });
  return flattenedList;
};
export const SearchList = React.memo(function SearchList({
  className,
  style,
  ...props
}: SearchListProps) {
  const mapData = useSearchPageMapData(state => state.mapData);
  const initialMapData = useSearchPageMapData(state_0 => state_0.initialMapData);
  const hasFirstClientDataLoaded = useSearchPageMapData(state_1 => state_1.hasFirstClientDataLoaded);
  const isMapDataFetching = useSearchPageMapData(state_2 => state_2.isMapDataFetching);
  const isTotalCountFetching = useSearchPageMapData(state_3 => state_3.isTotalCountFetching);
  const isAtLimit = useSearchPageMapData(state_4 => state_4.isAtLimit);
  const isInitialBoundsFetch = useSearchPageMapData(state_5 => state_5.isInitialBoundsFetch);
  const totalCountWithoutBounds = useSearchPageMapData(state_6 => state_6.totalCountWithoutBounds);

  // Get city and filters for title construction
  const pathname = usePathname();
  const {
    filters
  } = useUrlFilters();
  const isMobile = useIsMobile();

  // Extract city from pathname
  const city = React.useMemo(() => {
    const segments = pathname?.split("/").filter(Boolean) || [];
    return segments[0] || "bengaluru";
  }, [pathname]);
  const cityDisplayName = getCityNameFromCode(city);

  // Construct the title using common utility (counts are shown separately inline)
  const title = React.useMemo(() => {
    return getSearchTitle({
      cityDisplayName,
      filters
    });
  }, [filters, cityDisplayName]);

  // Create list data using useMemo to prevent infinite renders
  const listData = React.useMemo(() => getListData({
    mapData,
    initialMapData,
    hasFirstClientDataLoaded
  }), [mapData, initialMapData, hasFirstClientDataLoaded]);

  // Container ref for virtualizer
  const parentRef = React.useRef<HTMLDivElement>(null);

  // Track mount for initial size estimation; columns via media query
  const [isMounted, setIsMounted] = React.useState(false);
  const isXL = useMediaQuery("(min-width: 1280px)");
  const columnsCount = isXL ? 2 : 1;

  // Handle hydration
  React.useEffect(() => {
    setIsMounted(true);
  }, []);

  // Media query handles resize changes; no manual window listeners needed

  // Calculate total count of units from map data
  const totalPropertiesCount = listData.length;

  // Promotional cards integration - rotating cards for variety
  const {
    cards: rotatingCards
  } = useRotatingCards("search");
  type RowItem = {
    type: "properties";
    items: DerivedSupItem[];
    startIndex: number;
  } | {
    type: "promo-row";
    card: PromotionalCardConfig;
  };

  // Create rows based on current columns count
  // Insert promo row after every PROMO_INTERVAL property rows (5 rows = 10 items on 2-col)
  const PROMO_INTERVAL = 5;
  const gridRows = React.useMemo(() => {
    const result: RowItem[] = [];
    let propertyRowCount = 0;
    let promoCardIndex = 0;

    // Chunk properties into rows
    for (let i = 0; i < listData.length; i += columnsCount) {
      result.push({
        type: "properties",
        items: listData.slice(i, i + columnsCount),
        startIndex: i
      });
      propertyRowCount++;

      // Insert promo row after every PROMO_INTERVAL property rows
      if (rotatingCards.length > 0 && propertyRowCount % PROMO_INTERVAL === 0) {
        // Cycle through cards in sequence
        const card = rotatingCards[promoCardIndex % rotatingCards.length];
        result.push({
          type: "promo-row",
          card
        });
        promoCardIndex++;
      }
    }
    return result;
  }, [listData, columnsCount, rotatingCards]);

  // Create virtualizer
  const virtualizer = useVirtualizer({
    count: gridRows.length,
    getScrollElement: () => parentRef.current,
    estimateSize: index => {
      const row = gridRows[index];
      if (row.type === "promo-row") return PROMO_ROW_HEIGHT;
      return ITEM_HEIGHT;
    },
    overscan: 2
  });
  return <ErrorBoundary fallback={ListViewFallback}>
			<div className={`w-full ${className}`} style={style} {...props}>
				<div className="flex flex-col w-full relative h-full" ref={parentRef} style={{
        height: "100%",
        overflowY: "auto"
      }}>
					{/* Mobile: Sticky search header with search bar and filters */}
					{isMobile && totalPropertiesCount > 0 && <MobileSearchHeader title={title} propertyCount={totalPropertiesCount} totalCount={totalCountWithoutBounds} isLoading={isTotalCountFetching || isInitialBoundsFetch} isAtLimit={isAtLimit} />}

					{/* Desktop: Simple property count message */}
					{!isMobile && totalPropertiesCount > 0 && <div className="px-4 py-3 bg-gray-50">
							<p className="text-sm text-gray-600">
								{isTotalCountFetching ? <span className="inline-block h-3 bg-gray-300 rounded animate-pulse w-16 align-middle mr-1"></span> : totalCountWithoutBounds !== null && totalCountWithoutBounds > 0 ? <span className="font-semibold">{totalCountWithoutBounds.toLocaleString()}</span> : <span className="font-semibold">{totalPropertiesCount.toLocaleString()}</span>}{" "}
								<span className="font-medium">{title}</span>,{" "}
								<span className="font-semibold">
									showing{" "}
									{isInitialBoundsFetch ? <span className="inline-block h-3 bg-gray-300 rounded animate-pulse w-8 align-middle"></span> : <span className="font-semibold">
											{totalPropertiesCount.toLocaleString()}
											{isAtLimit ? "+" : ""}
										</span>}{" "}
									in the visible map area
								</span>
								{!isInitialBoundsFetch && isAtLimit ? ". Zoom in for a better search." : ""}
							</p>
						</div>}

					{isMapDataFetching && listData.length === 0 ? <div className="px-2">
							<ListSkeleton count={6} />
						</div> : listData.length > 0 ? <div className="w-full h-full">
							{!isMounted ? (/* SSR fallback */
          <div className="grid grid-cols-1 xl:grid-cols-2 gap-4 px-2 py-1">
									{gridRows.map((row_0, rowIndex) => {
              if (row_0.type === "promo-row" && row_0.card) {
                return <div key={`ssr-promo-${rowIndex}`} className="col-span-full px-2">
													<PromotionalCardRenderer card={row_0.card} />
												</div>;
              }
              if (row_0.type === "properties") {
                return row_0.items.map((item: DerivedSupItem, index_0: number) => <SearchItem data={item} rank={rowIndex * columnsCount + index_0 + 1} key={item._id || `ssr-${rowIndex}-${index_0}`} className="h-[370px]" />);
              }
              return null;
            })}
								</div>) : <div style={{
            height: `${virtualizer.getTotalSize()}px`,
            width: "100%",
            position: "relative"
          }}>
									{virtualizer.getVirtualItems().map(virtualRow => {
              const row_1 = gridRows[virtualRow.index];
              return <div key={virtualRow.key} style={{
                position: "absolute",
                top: 0,
                left: 0,
                width: "100%",
                height: `${virtualRow.size}px`,
                transform: `translateY(${virtualRow.start}px)`
              }}>
												{row_1.type === "promo-row" && row_1.card ? <div className="w-full px-2">
														<PromotionalCardRenderer card={row_1.card} />
													</div> : <div className="grid grid-cols-1 xl:grid-cols-2 gap-4 h-full px-2 py-1" style={{
                  gridTemplateColumns: `repeat(${columnsCount}, minmax(0, 1fr))`
                }}>
														{row_1.type === "properties" && row_1.items.map((item_0: DerivedSupItem, columnIndex: number) => <SearchItem data={item_0} rank={row_1.startIndex + columnIndex + 1} key={item_0._id || `${virtualRow.index}-${columnIndex}`} className="h-[370px]" />)}
													</div>}
											</div>;
            })}
								</div>}
						</div> : <div className="flex flex-col items-center justify-center h-full">
							<div className="text-center space-y-4 max-w-md mx-auto px-2">
								<div className="text-6xl mb-4">🏠</div>
								<h3 className="text-xl font-semibold text-gray-900">No properties found</h3>
								<p className="text-gray-600">
									We couldn&apos;t find any properties matching your current filters. Try adjusting your search criteria
									to see more results.
								</p>
								<div className="mt-6">
									<p className="text-sm text-gray-500 mb-2">Try:</p>
									<ul className="text-sm text-gray-600 space-y-1">
										<li>• Expanding your location search</li>
										<li>• Adjusting your budget range</li>
										<li>• Removing some filters</li>
										<li>• Changing property type</li>
									</ul>
								</div>
							</div>
						</div>}

					{/* Show loading indicator below data for initial bounds fetch */}
					{isMapDataFetching && isInitialBoundsFetch && listData.length > 0 && <div className="px-4 py-3 bg-gray-50 border-t">
							<div className="flex items-center justify-center">
								<LoaderCircleIcon className="animate-spin w-4 h-4 text-primary" />
								<span className="ml-2 text-sm text-gray-600">Loading more properties...</span>
							</div>
						</div>}

					{/* Show loading overlay only for subsequent fetches (non-initial) when we have existing data */}
					{isMapDataFetching && !isInitialBoundsFetch && listData.length > 0 && <div className="absolute inset-0 bg-white/70 backdrop-blur-sm z-20 flex items-start justify-center pt-4">
							<div className="flex items-center">
								<LoaderCircleIcon className="animate-spin w-5 h-5 text-primary" />
								<span className="ml-2 text-body text-neutral-600">Loading properties...</span>
							</div>
						</div>}
				</div>
			</div>
		</ErrorBoundary>;
});